/**
 * Operations Insights — types and lens definitions.
 *
 * A "lens" is a subset of visit statuses whose minutes are summed to define
 * "time spent." The backend returns minutes per (coordinator, study, status);
 * every lens is a client-side sum, so switching lenses never refetches and
 * new lenses apply retroactively to all history.
 */

// ---- API row shapes (GET /api/ops-insights/*) -----------------------------

export interface EffortRow {
  crc_name: string; // normalized (lower/trim); '' = unattributed
  user_id: string | null; // null = CRC name not mapped to a user
  study_name: string; // raw appointment string
  protocol_id: string | null; // null = study name not mapped to the registry
  status: string;
  total_minutes: number;
}

export interface VisitStatsRow {
  crc_name: string;
  user_id: string | null;
  study_name: string;
  protocol_id: string | null;
  visit_count: number;
  completed_count: number;
  in_flight_count: number;
  incomplete_count: number;
  override_minutes: number;
  override_count: number;
}

export interface StudyInfo {
  id: string;
  protocol_number: string;
  study_nickname: string | null;
  sponsor_name: string | null;
  status: string;
  enrollment_status: string | null;
  phase: string | null;
  therapeutic_area: string | null;
  ereg_config: { primary_investigator_id: string | null; primary_crc_id: string | null } | null;
  team: {
    id: string;
    user_id: string;
    role: string;
    status: string;
    user: { id: string; full_name: string; email: string | null } | null;
  }[];
}

export interface Coordinator {
  id: string;
  full_name: string;
  email: string | null;
  role: string;
  crc_names: { id: string; crc_name: string }[];
}

export interface DelegationEntry {
  id: string;
  user_id: string;
  user: { id: string; full_name: string; email: string | null } | null;
  role: string;
  role_other: string | null;
  status: string;
  start_date: string;
  end_date: string | null;
  is_currently_delegated: boolean;
}

export interface DelegationProtocol {
  protocol_id: string;
  has_doa_log: boolean;
  entries: DelegationEntry[];
}

export interface PortalAccessRow {
  id: string;
  user_id: string;
  protocol_id: string | null;
  bookmark_id: string;
  bookmark_type: string | null;
  label: string | null;
  granted_at: string;
  note: string | null;
}

// ---- Lenses ---------------------------------------------------------------

export type TimeLens = 'with_crc' | 'ready_crc' | 'whole_visit' | 'breakdown';

/**
 * Explicit semantic status subsets — only for the two "narrow" lenses.
 * `whole_visit` and `breakdown` are deliberately NOT hand-maintained status
 * lists (Fix Spec D): a hard-coded "counted" list is exactly the drift-prone
 * pattern the server side already removed (`ops_excluded_statuses()`), and
 * mirroring it here would silently drop any status added to the workflow
 * enum without a matching frontend edit. Instead, `lensTotal` sums EVERY key
 * present in a given `minutesByStatus` record for those two lenses — the
 * server already excludes the excluded-status set, so whatever is left is
 * countable by construction. See `lensTotal` below.
 */
export const LENS_STATUSES: Record<'with_crc' | 'ready_crc', string[]> = {
  with_crc: ['with_crc'],
  ready_crc: ['in_room', 'with_crc'],
};

export const LENS_LABELS: Record<TimeLens, string> = {
  with_crc: 'With CRC',
  ready_crc: 'Ready + CRC',
  whole_visit: 'Whole visit',
  breakdown: 'Breakdown',
};

/** Statuses a with_crc/ready_crc lens sums. whole_visit/breakdown have no
 *  fixed list — `lensTotal` sums every key present instead. */
export function lensStatuses(lens: 'with_crc' | 'ready_crc'): string[] {
  return LENS_STATUSES[lens];
}

/**
 * Sum a minutes-by-status record under a lens. `with_crc`/`ready_crc` are
 * explicit semantic subsets (`LENS_STATUSES`, unaffected by unknown keys);
 * `whole_visit`/`breakdown` sum every key present in `minutesByStatus`
 * (Fix Spec D) — a synthetic/new status contributes to those totals with
 * zero frontend changes, and (via StatusBreakdownBar's "Other" segment) is
 * never silently dropped from the display either.
 */
export function lensTotal(minutesByStatus: Record<string, number>, lens: TimeLens): number {
  if (lens === 'whole_visit' || lens === 'breakdown') {
    let sum = 0;
    for (const m of Object.values(minutesByStatus)) sum += Number(m || 0);
    return sum;
  }
  return lensStatuses(lens).reduce((sum, s) => sum + (minutesByStatus[s] || 0), 0);
}

// Visit-status display order + labels + data colors (domain colors, not
// chrome — mirrors Status Board's per-status color coding).
export const STATUS_META: { key: string; label: string; barClass: string }[] = [
  { key: 'lobby', label: 'Lobby', barClass: 'bg-amber-400' },
  { key: 'in_room', label: 'Ready CRC', barClass: 'bg-sky-400' },
  { key: 'with_crc', label: 'With CRC', barClass: 'bg-primary-600' },
  { key: 'waiting_on_inv', label: 'Waiting on Inv', barClass: 'bg-orange-400' },
  { key: 'with_investigator', label: 'With Investigator', barClass: 'bg-purple-500' },
  { key: 'done', label: 'Wrap-up', barClass: 'bg-green-500' },
];

// ---- Ranges ---------------------------------------------------------------

export type RangeKey = '7d' | '30d' | '90d' | 'custom';

/**
 * Format a Date as a site-local YYYY-MM-DD (`en-CA` gives ISO ordering) for
 * the given IANA tz, falling back to the browser's local timezone when `tz`
 * is absent or invalid. `sites.timezone` is free text, not validated at
 * write time, so `Intl.DateTimeFormat` can throw on construction — guarded.
 */
function formatSiteLocalDate(d: Date, tz?: string | null): string {
  if (tz) {
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(d);
    } catch {
      // Unknown/invalid IANA name in the free-text sites.timezone column —
      // fall through to the browser-local formatting below.
    }
  }
  return d.toLocaleDateString('en-CA');
}

/** Site-local "today" (see `formatSiteLocalDate`) — used to detect a
 *  site-local day rollover so preset ranges can be recomputed at read time
 *  instead of freezing at whenever the provider happened to mount. */
export function siteLocalToday(tz?: string | null): string {
  return formatSiteLocalDate(new Date(), tz);
}

/**
 * Inclusive `days`-day window ending "today", in site-local calendar dates.
 * `tz` is an optional IANA name (typically `currentSite.timezone`);
 * invalid/unknown values fall back to the browser's local timezone (see
 * `formatSiteLocalDate`). Call this at read time, not once at mount — a
 * stored `from`/`to` computed once goes stale the moment the site-local day
 * rolls over.
 */
export function rangeDates(key: Exclude<RangeKey, 'custom'>, tz?: string | null): { from: string; to: string } {
  const days = key === '7d' ? 7 : key === '30d' ? 30 : 90;
  const to = formatSiteLocalDate(new Date(), tz); // site-local "today"

  // Derive `from` by subtracting (days - 1) calendar days from `to` — but do
  // the subtraction on a Date anchored at 12:00 UTC on `to`'s date parts,
  // never on a browser-local `Date`. A browser-local Date's day-of-month
  // arithmetic (`setDate`) walks the BROWSER's calendar, so when the browser
  // tz and the site tz disagree about a DST transition landing between
  // `from` and `to`, formatting the result back into the site tz could land
  // one day off. Noon UTC is never within hours of any real-world DST
  // transition (those all land near local midnight), so `setUTCDate` on a
  // UTC-noon anchor can never skip or repeat a calendar day, in any
  // timezone — it's pure calendar arithmetic, immune to DST.
  const [y, m, d] = to.split('-').map(Number);
  const anchor = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  anchor.setUTCDate(anchor.getUTCDate() - (days - 1));
  const from = `${anchor.getUTCFullYear()}-${String(anchor.getUTCMonth() + 1).padStart(2, '0')}-${String(anchor.getUTCDate()).padStart(2, '0')}`;

  return { from, to };
}

/** "3h 25m" / "45m" display for minute totals. */
export function formatMinutes(minutes: number): string {
  const m = Math.round(minutes);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
